import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import type { CookieConsentProductResult, CookieConsentState } from '@/types/cookieConsent';
import {
  initialiseCookieConsent,
  hasPolicyConsent,
  showCookiePreferences,
  isSdkAvailable,
} from '@/services/cookieConsentService';

const noop = () => {};

const CookieConsentContext = createContext<CookieConsentState>({
  isResolved: false,
  consent: null,
  hasAnalyticsConsent: false,
  hasFunctionalConsent: false,
  hasMarketingConsent: false,
  sdkAvailable: false,
  reopenPreferences: noop,
});

export function useCookieConsent(): CookieConsentState {
  return useContext(CookieConsentContext);
}

interface CookieConsentProviderProps {
  readonly children: ReactNode;
}

export function CookieConsentProvider({ children }: CookieConsentProviderProps) {
  const [consent, setConsent] = useState<CookieConsentProductResult | null>(null);
  const [isResolved, setIsResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      const result = await initialiseCookieConsent();
      if (!cancelled) {
        setConsent(result);
        setIsResolved(true);
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleReopenPreferences = useCallback(() => {
    if (!isSdkAvailable()) {
      toast.error('Cookie preferences are unavailable. The cookie consent service could not be loaded.');
      return;
    }
    showCookiePreferences();
  }, []);

  const state = useMemo<CookieConsentState>(() => ({
    isResolved,
    consent,
    hasAnalyticsConsent: hasPolicyConsent(consent, 'analytics'),
    hasFunctionalConsent: hasPolicyConsent(consent, 'functional'),
    hasMarketingConsent: hasPolicyConsent(consent, 'marketing'),
    sdkAvailable: isSdkAvailable(),
    reopenPreferences: handleReopenPreferences,
  }), [isResolved, consent, handleReopenPreferences]);

  return (
    <CookieConsentContext.Provider value={state}>
      {children}
    </CookieConsentContext.Provider>
  );
}
